import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { AuthService } from '../services/auth.service';

@Injectable({
  providedIn: 'root'
})
export class AuthGuard implements CanActivate {
  constructor(
    private authService: AuthService,
    private router: Router
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    // Gate synchronously on the locally-held JWT. The old guard fired a blocking
    // GET /auth/me on every navigation and returned that Observable to the router,
    // which made navigation async. Under zoneless change detection, once the app
    // has been idle (fully stable) a single click resolves the guard after its
    // change-detection pass has already finished, so the route activates but the
    // RouterOutlet never repaints until a manual refresh. Keeping the guard
    // synchronous means navigation completes inside the click's change-detection
    // pass. The server still validates the token on every real API call, and the
    // jwt interceptor handles 401s, so we lose no security by not re-verifying here.
    // hasSession(), not isLoggedIn(): the access token lapses long before the session
    // does, so gating on isLoggedIn() bounced people to /login for a session the server
    // would have renewed happily. The first API call on the restored route drives the
    // refresh, and a truly dead session still ends up at /login via the interceptor.
    if (!this.authService.hasSession()) {
      this.router.navigate(['/login'], { queryParams: { returnUrl: state.url } });
      return false;
    }

    // Role gating is read straight from the token/cached user (see
    // AuthService.getUserRole), so it stays synchronous too.
    //
    // But the role claim lives in the ACCESS token, and hasSession() admits users whose
    // access token has expired and not yet been replaced. Failing closed there would
    // bounce them off every role-gated route on the way back from a lapse. When we cannot
    // read a role at all, defer the check: the route activates, its first API call
    // refreshes the token, and the server still enforces the role on every request.
    const roleKnown = this.authService.getUserRole() !== null;

    const requiredRole = route.data?.['role'] as string | undefined;
    if (roleKnown && requiredRole && !this.authService.hasRole(requiredRole)) {
      this.router.navigate(['/']);
      return false;
    }

    // ...or any one of a set of roles (e.g. Executive OR SysAdmin).
    const requiredRoles = route.data?.['roles'] as string[] | undefined;
    if (roleKnown && requiredRoles && requiredRoles.length > 0 && !this.authService.hasAnyRole(requiredRoles)) {
      this.router.navigate(['/']);
      return false;
    }

    return true;
  }
}
